import { createSelector } from '@ngrx/store';

import { selectBlueSageCardRailsAvailable } from '../../bluesage/bluesage-job.selectors';
import { hasLiveInvoice } from '../../invoices/invoices.utils';
import { depositPosition, selectDefaultDepositMinor, selectDepositRequiredToBook } from '../../job/job-deposit.selectors';
import { createCheckSinglePermissionSelector, createConfigSelector } from '../../state/branding.store';
import { selectJobCollectibles, selectTransactionJob } from '../transaction-job.selectors';

import { createTransactionDialogFeature } from './create-transaction-dialog.feature';
import { MANUAL_RECORD_METHODS, predictAutoAppliedMinor } from './create-transaction-dialog.util';

export const selectCustomerReachable = createSelector(
    selectTransactionJob,
    createTransactionDialogFeature.selectCustomerId,
    (job, customerId) => {
        const user = job?.users?.find((u) => u.user?.id === customerId)?.user
            ?? job?.users?.find((u) => u.role === 'customer')?.user;

        return Boolean(user?.phone || user?.email);
    },
);

// Requirement: a deposit may be taken on every job, including one that
// requires none or is already paid up — an operator offering money up front
// is the ordinary case, so with nothing outstanding this falls back to the
// zone's default deposit rather than disappearing.
const selectDepositPosition = createSelector(
    selectTransactionJob,
    selectDefaultDepositMinor,
    selectDepositRequiredToBook,
    depositPosition,
);

export const selectDepositOffer = createSelector(
    selectDepositPosition,
    selectDefaultDepositMinor,
    ({ outstandingMinor, requiredMinor, source }, defaultDepositMinor) => {
        if (outstandingMinor > 0) { return { amountMinor: outstandingMinor, source }; }

        if (requiredMinor > 0) { return { amountMinor: requiredMinor, source }; }

        return { amountMinor: defaultDepositMinor, source: 'default' as const };
    },
);

const selectNewInvoiceBalance = createSelector(
    selectTransactionJob,
    selectJobCollectibles,
    (job, collectibles) => (eventIds: string[]) => {
        const invoiceTotalMinor = (collectibles?.invoiceableEvents ?? [])
            .filter(({ id }) => eventIds.includes(id))
            .reduce((acc, event) => acc + event.totalMinor, 0);

        const appliedMinor = predictAutoAppliedMinor({
            invoiceTotalMinor,
            currency: job?.currency,
            jobHasLiveInvoice: collectibles?.hasAnyInvoices ?? false,
            transactions: job?.transactions ?? [],
        });

        return Math.max(0, invoiceTotalMinor - appliedMinor);
    },
);

// Requirement: crew leads raise the invoice for the work they just finished while
// taking the payment for it, but only someone allowed to create invoices may.
export const selectNewInvoiceOffer = createSelector(
    selectJobCollectibles,
    selectNewInvoiceBalance,
    createCheckSinglePermissionSelector({ permission: 'invoice.create' }),
    createConfigSelector('INVOICE_DISABLEMULTIPLEEVENTS'),
    (collectibles, balanceOf, mayCreateInvoice, oneEventPerInvoice) => {
        const events = (collectibles?.invoiceableEvents ?? []).filter(({ totalMinor }) => totalMinor > 0);

        if (!mayCreateInvoice || !events.length) { return undefined; }

        const singleSelect = Boolean(oneEventPerInvoice);
        const firstCollectible = events.find(({ id }) => balanceOf([ id ]) > 0);

        if (singleSelect && !firstCollectible) { return undefined; }

        const defaultEventIds = singleSelect ? [ firstCollectible.id ] : events.map(({ id }) => id);

        const balanceMinor = balanceOf(defaultEventIds);

        if (balanceMinor <= 0) { return undefined; }

        return {
            events,
            singleSelect,
            defaultEventIds,
            askForEvents: events.length > 1,
            balanceMinor,
        };
    },
);

// Requirement: the invoice is looked up live, never held in dialog state —
// one left open while the invoice is paid elsewhere must not keep collecting
// against it. A gone invoice resolves to `undefined` and the selection
// collapses to nothing.
export const selectSelectedTargets = createSelector(
    selectJobCollectibles,
    selectTransactionJob,
    selectDepositOffer,
    selectNewInvoiceBalance,
    createTransactionDialogFeature.selectTarget,
    createTransactionDialogFeature.selectNewInvoiceEventIds,
    createTransactionDialogFeature.selectRaisedInvoiceId,
    (collectibles, job, depositOffer, balanceOf, target, chosenEventIds, raisedForThisDialog) => {
        const openInvoices = collectibles?.openInvoices ?? [];

        const newInvoiceSelected = target?.kind === 'newInvoice';

        // Requirement: once the invoice is raised, every retry is posted against it — an
        // attempt that was declined, or failed after the invoice was raised, must never
        // raise a second one.
        const raisedInvoiceId = newInvoiceSelected
            ? raisedForThisDialog ?? invoiceRaisedFor(job?.events ?? [], chosenEventIds)
            : undefined;

        const targetInvoiceId = target?.kind === 'invoice' ? target.invoiceId : raisedInvoiceId;

        const invoice = targetInvoiceId
            ? openInvoices.find(({ id }) => id === targetInvoiceId)
            : undefined;

        const newInvoiceEventIds = newInvoiceSelected && !raisedInvoiceId
            ? chosenEventIds.filter((id) => collectibles?.invoiceableEvents.some((event) => event.id === id))
            : undefined;

        const depositSelected = target?.kind === 'deposit';
        const depositMinor = depositSelected ? depositOffer.amountMinor : 0;

        const newInvoiceMinor = newInvoiceEventIds ? balanceOf(newInvoiceEventIds) : 0;

        return {
            depositSelected,
            depositMinor,
            invoice,
            newInvoiceSelected,
            newInvoiceEventIds,
            totalMinor: depositMinor + (invoice?.balanceMinor ?? 0) + newInvoiceMinor,
            invoiceId: invoice?.id ?? raisedInvoiceId,
        };
    },
);

function invoiceRaisedFor(
    events: { id: string; invoices?: { id: string; deletedAt?: number; voidedAt?: number }[] }[],
    eventIds: string[],
): string | undefined {
    const chosen = events.filter(({ id }) => eventIds.includes(id));

    if (!chosen.length || !chosen.every(hasLiveInvoice)) { return undefined; }

    const [ first, ...rest ] = chosen.map((event) =>
        event.invoices.find((i) => !i.deletedAt && !i.voidedAt).id);

    return rest.every((id) => id === first) ? first : undefined;
}

// Requirement: an explicit hold chosen from the row menu is revalidated
// against the job's live holds, not trusted outright — one already completed
// or released elsewhere must resolve to `undefined` rather than still be
// actioned.
export const selectCompleteTarget = createSelector(
    selectJobCollectibles,
    createTransactionDialogFeature.selectOpenPreauthTransactionId,
    (collectibles, chosenId) => {
        const holds = collectibles?.openPreauths ?? [];

        if (chosenId) { return holds.find((hold) => hold.id === chosenId); }

        return holds.length === 1 ? holds[0] : undefined;
    },
);

// Requirement: on a dual-pricing location, the card price stays visible from
// the moment there's an amount through method selection, dropping only on a
// manual method's own screen (MANUAL_RECORD_METHODS) — gated on the STEP, not
// the method, since Back leaves the method set.
export const selectMoneyGoingOnCard = createSelector(
    createTransactionDialogFeature.selectMethod,
    createTransactionDialogFeature.selectStep,
    selectBlueSageCardRailsAvailable,
    (method, step, cardRailsAvailable) => step === 'complete'
        || (cardRailsAvailable
            && !(step === 'details' && method && MANUAL_RECORD_METHODS.includes(method))),
);
